import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import TableViewRounded from '@mui/icons-material/TableViewRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  ListSubheader,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { EmptyState, PageHeader } from '../../components/states';
import { useNotify } from '../../lib/notify';
import { SectionCard } from '../shop/ui';
import {
  dataKeys,
  downloadErrors,
  downloadTemplate,
  isBusy,
  startImport,
  uploadImport,
  useDataModel,
  useImportJob,
  type DataColumn,
  type DataModel,
  type ImportJob,
  type ImportMode,
  type ImportUpload,
  type MissingRef,
} from './api';

const IGNORE = '__ignore';
const ACCEPT =
  '.xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv';

const MODES: { value: ImportMode; label: string }[] = [
  { value: 'create_update', label: 'Create new records and update existing ones' },
  { value: 'create_only', label: 'Only create new records' },
  { value: 'update_only', label: 'Only update existing records' },
];

const MISSING: { value: MissingRef; label: string }[] = [
  { value: 'error', label: 'Stop that row with an error' },
  { value: 'skip_field', label: 'Leave the field empty' },
  { value: 'skip_row', label: 'Skip the row' },
  { value: 'create', label: 'Create it' },
];

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/[\s_]+/g, ' ')
    .replace(/\s*\/\s*/g, '/')
    .trim();

/** The mapping the API suggested, or (after a reload) the same match by path or label. */
function initialMapping(job: ImportJob, columns: DataColumn[], suggested?: (string | null)[]) {
  if (job.mapping) return job.mapping;
  if (suggested) return suggested;
  const byName = new Map<string, string>();
  for (const c of columns) {
    byName.set(norm(c.path), c.path);
    byName.set(norm(c.label), c.path);
  }
  return job.headers.map((h) => byName.get(norm(h)) ?? null);
}

/** /data/import/:model — upload, map the columns, test, import, then the problems per row. */
export function ImportPage() {
  const { model: name = '' } = useParams();
  const { model, isLoading } = useDataModel(name);
  const navigate = useNavigate();

  if (isLoading) return <LinearProgress />;
  if (!model?.canImport) {
    return (
      <EmptyState
        icon={<UploadFileRounded />}
        title="Import is not available"
        body="This list cannot be imported, or your role does not allow it."
        action={{ label: 'Go back', onClick: () => void navigate(-1) }}
      />
    );
  }
  return <ImportWizard key={model.name} model={model} />;
}

function ImportWizard({ model }: { model: DataModel }) {
  const notify = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const jobId = params.get('job');
  const [upload, setUpload] = useState<ImportUpload | null>(null);
  // Kept so another sheet of the same workbook can be uploaded without picking the file again.
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const job = useImportJob(jobId);
  const current = job.data ?? (upload?.id === jobId ? upload : undefined);

  const setJob = (id: string | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set('job', id);
    else p.delete('job');
    setParams(p, { replace: true });
  };

  const send = (file: File, sheet?: string) => {
    setUploading(true);
    uploadImport(model.name, file, sheet)
      .then((res) => {
        setFile(file);
        setUpload(res);
        queryClient.setQueryData(dataKeys.job(res.id), res);
        setJob(res.id);
      })
      .catch(notify.error)
      .finally(() => setUploading(false));
  };

  const template = (format: 'xlsx' | 'csv') =>
    void downloadTemplate(model.name, format).catch(notify.error);

  return (
    <>
      <PageHeader
        title={`Import ${model.label.toLowerCase()}`}
        subtitle="Upload an Excel or CSV file, match its columns, test it, then import. A test saves nothing."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<ArrowBackRounded />}
              onClick={() => void navigate(-1)}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              startIcon={<TableViewRounded />}
              onClick={() => template('xlsx')}
            >
              Template
            </Button>
          </>
        }
      />
      {!jobId || (!current && !job.isLoading) ? (
        <UploadCard
          busy={uploading}
          onFile={(f) => send(f)}
          onTemplate={template}
          missing={!!jobId && job.isError}
        />
      ) : !current ? (
        <LinearProgress />
      ) : (
        <JobView
          key={current.id}
          model={model}
          job={current}
          upload={upload?.id === current.id ? upload : null}
          uploading={uploading}
          onSheet={file ? (sheet) => send(file, sheet) : undefined}
          onReset={() => {
            setUpload(null);
            setFile(null);
            setJob(null);
          }}
        />
      )}
    </>
  );
}

function UploadCard({
  busy,
  onFile,
  onTemplate,
  missing,
}: {
  busy: boolean;
  onFile: (file: File) => void;
  onTemplate: (format: 'xlsx' | 'csv') => void;
  missing: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  };
  return (
    <SectionCard
      title="Choose a file"
      icon={<UploadFileRounded />}
      description="Excel (.xlsx) or CSV, up to 10 MB and 20,000 rows. The first row holds the column names; the lines of a document or a product's variants go on the rows under it."
    >
      {missing && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          That import was not found. Upload the file again.
        </Alert>
      )}
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
        sx={{
          border: '2px dashed',
          borderColor: over ? 'primary.main' : 'divider',
          borderRadius: 3,
          py: { xs: 4, sm: 6 },
          px: 2,
          textAlign: 'center',
          bgcolor: over ? 'action.hover' : 'transparent',
        }}
      >
        <Typography sx={{ mb: 2 }} color="text.secondary">
          Drop the file here, or
        </Typography>
        <Button variant="contained" loading={busy} onClick={() => input.current?.click()}>
          Choose file
        </Button>
        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          hidden
          data-testid="import-file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) onFile(file);
          }}
        />
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Not sure of the columns? Download a template:
        </Typography>
        <Button size="small" onClick={() => onTemplate('xlsx')}>
          Excel
        </Button>
        <Button size="small" onClick={() => onTemplate('csv')}>
          CSV
        </Button>
      </Stack>
    </SectionCard>
  );
}

function JobView({
  model,
  job,
  upload,
  uploading,
  onSheet,
  onReset,
}: {
  model: DataModel;
  job: ImportJob;
  upload: ImportUpload | null;
  uploading: boolean;
  onSheet?: (sheet: string) => void;
  onReset: () => void;
}) {
  const notify = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const columns = upload?.columns ?? model.columns;
  const [mapping, setMapping] = useState<(string | null)[]>(() =>
    initialMapping(job, columns, upload?.suggestedMapping),
  );
  const [mode, setMode] = useState<ImportMode>(job.options?.mode ?? 'create_update');
  const [missing, setMissing] = useState<Record<string, MissingRef>>(
    job.options?.onMissingRef ?? {},
  );
  const [starting, setStarting] = useState<'test' | 'run' | null>(null);

  const byPath = new Map(columns.map((c) => [c.path, c]));
  const busy = isBusy(job);
  const done = job.status === 'DONE';
  const mapped = new Set(mapping.filter((m): m is string => m !== null));
  const refColumns = [...mapped]
    .map((p) => byPath.get(p))
    .filter((c): c is DataColumn => !!c && !!c.ref && c.kind !== 'id');
  const missingRequired =
    mode === 'update_only' || mapped.has('id')
      ? []
      : columns.filter((c) => c.required && !c.child && !mapped.has(c.path));

  const start = (dryRun: boolean) => {
    setStarting(dryRun ? 'test' : 'run');
    const onMissingRef = Object.fromEntries(Object.entries(missing).filter(([p]) => mapped.has(p)));
    startImport(job.id, dryRun, { mapping, options: { mode, onMissingRef } })
      .then((res) => {
        queryClient.setQueryData(dataKeys.job(job.id), res);
        void queryClient.invalidateQueries({ queryKey: dataKeys.jobs, exact: true });
      })
      .catch(notify.error)
      .finally(() => setStarting(null));
  };

  const groups = [
    { key: '', label: model.label, columns: columns.filter((c) => !c.child) },
    ...model.children.map((ch) => ({
      key: ch.key,
      label: ch.label,
      columns: columns.filter((c) => c.child === ch.key),
    })),
  ].filter((g) => g.columns.length > 0);

  const sample = (i: number) =>
    (upload?.sampleRows ?? [])
      .map((r) => r[i] ?? '')
      .filter((v) => v !== '')
      .slice(0, 3)
      .join(', ');

  return (
    <Stack spacing={2.5}>
      <SectionCard
        title={job.fileName}
        icon={<UploadFileRounded />}
        description={`${String(job.rowCount)} ${job.rowCount === 1 ? 'row' : 'rows'}${job.sheet ? ` · sheet ${job.sheet}` : ''}`}
        action={
          !busy && (
            <Button variant="outlined" onClick={onReset} disabled={uploading}>
              {done ? 'Import another file' : 'Choose another file'}
            </Button>
          )
        }
      >
        {onSheet && upload && upload.sheets.length > 1 && !busy && !done && (
          <TextField
            select
            size="small"
            label="Sheet"
            value={job.sheet ?? upload.sheets[0] ?? ''}
            disabled={uploading}
            onChange={(e) => onSheet(e.target.value)}
            sx={{ minWidth: 200, mb: 1.5 }}
          >
            {upload.sheets.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        )}
        {busy ? (
          <Progress job={job} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {done
              ? 'This file has been imported.'
              : 'Match each column of the file to a field. Columns set to "Do not import" are ignored.'}
          </Typography>
        )}
      </SectionCard>

      {!done && !busy && (
        <SectionCard title="Columns" icon={<FactCheckRounded />}>
          <TableContainer>
            <Table size="small" aria-label="Column mapping">
              <TableHead>
                <TableRow>
                  <TableCell>File column</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Sample</TableCell>
                  <TableCell sx={{ width: { sm: '45%' } }}>Import as</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {job.headers.map((h, i) => (
                  <TableRow key={`${String(i)}-${h}`}>
                    <TableCell sx={{ fontWeight: 600 }}>{h || `Column ${String(i + 1)}`}</TableCell>
                    <TableCell
                      sx={{
                        display: { xs: 'none', sm: 'table-cell' },
                        color: 'text.secondary',
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {sample(i)}
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={mapping[i] ?? IGNORE}
                        slotProps={{ htmlInput: { 'aria-label': `Import ${h} as` } }}
                        onChange={(e) => {
                          const next = [...mapping];
                          next[i] = e.target.value === IGNORE ? null : e.target.value;
                          setMapping(next);
                        }}
                      >
                        <MenuItem value={IGNORE}>
                          <em>Do not import</em>
                        </MenuItem>
                        {groups.flatMap((g) => [
                          <ListSubheader key={`g-${g.key}`}>{g.label}</ListSubheader>,
                          ...g.columns.map((c) => (
                            <MenuItem
                              key={c.path}
                              value={c.path}
                              disabled={mapped.has(c.path) && mapping[i] !== c.path}
                            >
                              {g.key ? c.label.slice(g.label.length + 3) : c.label}
                              {c.required ? ' *' : ''}
                            </MenuItem>
                          )),
                        ])}
                      </TextField>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {missingRequired.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Not mapped: {missingRequired.map((c) => c.label).join(', ')}. New records need these;
              existing records are found by {model.keyLabel.toLowerCase()} or External ID.
            </Alert>
          )}

          <Stack spacing={2} sx={{ mt: 3 }}>
            <TextField
              select
              size="small"
              label="What to do"
              value={mode}
              onChange={(e) => setMode(e.target.value as ImportMode)}
              sx={{ maxWidth: 420 }}
            >
              {MODES.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
            {refColumns.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  When a related record is not found
                </Typography>
                <Stack spacing={1.25}>
                  {refColumns.map((c) => (
                    <TextField
                      key={c.path}
                      select
                      size="small"
                      label={c.label}
                      value={missing[c.path] ?? 'error'}
                      onChange={(e) =>
                        setMissing({ ...missing, [c.path]: e.target.value as MissingRef })
                      }
                      sx={{ maxWidth: 420 }}
                    >
                      {MISSING.filter((o) => o.value !== 'create' || c.canCreateRef).map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>

          <Stack direction="row" spacing={1.25} sx={{ mt: 3, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              loading={starting === 'test'}
              disabled={!!starting || mapped.size === 0}
              onClick={() => start(true)}
            >
              Test
            </Button>
            <Button
              variant="contained"
              loading={starting === 'run'}
              disabled={!!starting || mapped.size === 0}
              onClick={() => start(false)}
            >
              Import
            </Button>
          </Stack>
        </SectionCard>
      )}

      {!busy && job.counts && <Results job={job} />}
      {done && (
        <Stack direction="row" spacing={1.25} sx={{ justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={() => void navigate(-1)}>
            Done
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

function Progress({ job }: { job: ImportJob }) {
  const pct = job.rowCount ? Math.min(100, (job.progress / job.rowCount) * 100) : 0;
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {job.status === 'TESTING' ? 'Testing' : 'Importing'}… {String(job.progress)} of{' '}
        {String(job.rowCount)} rows
      </Typography>
      <LinearProgress variant="determinate" value={pct} aria-label="Import progress" />
    </Box>
  );
}

function Count({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color?: 'success' | 'warning' | 'error' | 'info';
}) {
  return (
    <Chip
      label={`${label}: ${String(value)}`}
      color={value > 0 ? color : undefined}
      variant={value > 0 ? 'filled' : 'outlined'}
      size="small"
    />
  );
}

function Results({ job }: { job: ImportJob }) {
  const notify = useNotify();
  const counts = job.counts;
  if (!counts) return null;
  const test = job.dryRun;
  const errors = job.problems.filter((p) => p.status === 'error').length;
  let summary: ReactNode;
  if (job.status === 'FAILED') {
    summary = (
      <Alert severity="error">
        {test ? 'The test' : 'The import'} stopped: {job.error ?? 'unexpected error'}.
        {!test && ' Records saved before it stopped are kept.'}
      </Alert>
    );
  } else if (test) {
    summary = (
      <Alert severity={errors ? 'warning' : 'success'}>
        {errors
          ? `The test found problems in ${String(errors)} ${errors === 1 ? 'record' : 'records'}. Fix the file, or import now and only the good rows are saved.`
          : 'The test passed. Nothing has been saved yet: press Import.'}
      </Alert>
    );
  } else {
    summary = (
      <Alert severity={errors ? 'warning' : 'success'}>
        {errors
          ? `Imported, except ${String(errors)} ${errors === 1 ? 'record' : 'records'} with problems. Download them, fix and import that file.`
          : 'Imported.'}
      </Alert>
    );
  }
  const verb = test ? 'Would be ' : '';
  return (
    <SectionCard title={test ? 'Test result' : 'Import result'} icon={<FactCheckRounded />}>
      <Stack spacing={2}>
        {summary}
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Count label={`${verb}created`} value={counts.created} color="success" />
          <Count label={`${verb}updated`} value={counts.updated} color="info" />
          <Count label="Skipped" value={counts.skipped} color="warning" />
          <Count label="Problems" value={counts.failed} color="error" />
          {counts.refsCreated > 0 && (
            <Count
              label={`Related records ${test ? 'to create' : 'created'}`}
              value={counts.refsCreated}
              color="info"
            />
          )}
        </Stack>
        {job.problems.length > 0 && (
          <>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader aria-label="Problems">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 70 }}>Row</TableCell>
                    <TableCell sx={{ width: 90 }}>Result</TableCell>
                    <TableCell>Problem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {job.problems.map((p) => (
                    <TableRow key={p.row}>
                      <TableCell>
                        {p.rows > 1 ? `${String(p.row)}–${String(p.row + p.rows - 1)}` : p.row}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={p.status === 'error' ? 'error' : 'warning'}
                          label={p.status === 'error' ? 'Error' : 'Skipped'}
                        />
                      </TableCell>
                      <TableCell>
                        {p.messages.map((m, i) => (
                          <Typography key={i} variant="body2">
                            {m.column && <strong>{m.column}: </strong>}
                            {m.message}
                          </Typography>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                The rows with problems, with an Error column:
              </Typography>
              <Button
                size="small"
                onClick={() => void downloadErrors(job.id, 'xlsx').catch(notify.error)}
              >
                Excel
              </Button>
              <Button
                size="small"
                onClick={() => void downloadErrors(job.id, 'csv').catch(notify.error)}
              >
                CSV
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </SectionCard>
  );
}
